import { useState } from 'react';
import { AlertCircle, AlertTriangle, Download } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { SectionHeader } from '@/components/ui/section-header';
import { useInventoryStatus } from '@/hooks/useInventory';
import { exportInventoryIssues } from '@services/stock';

/**
 * Stock-file issues from the latest upload — invalid prices, bad barcodes,
 * negative quantities, file-level errors. Unlike product-data gaps (covered
 * by ProfileCompletenessBar, which has in-dashboard Fix CTAs) these are
 * fixed in the retailer's POS, so there's no "Fix" button — just guidance
 * and a download of the affected rows.
 *
 * Renders nothing when the latest feed came through clean.
 */
function SeverityIcon({ severity }) {
	return severity === 'error' ? (
		<AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
	) : (
		<AlertCircle className="h-4 w-4 shrink-0 text-warning-soft-foreground" />
	);
}

function IssueRow({ shopId, issue }) {
	const [downloading, setDownloading] = useState(false);

	const download = async () => {
		if (!shopId) return;
		setDownloading(true);
		try {
			const { url } = await exportInventoryIssues(shopId, issue.issueType);
			if (url) window.open(url, '_blank', 'noopener');
		} catch (e) {
			window.alert(`Couldn't prepare that download: ${e?.response?.data?.message ?? e.message}`);
		} finally {
			setDownloading(false);
		}
	};

	return (
		<div className="flex items-start gap-4 px-5 py-3.5">
			<div className="mt-0.5">
				<SeverityIcon severity={issue.severity} />
			</div>
			<div className="min-w-0 flex-1">
				<div className="text-sm font-semibold text-foreground">{issue.title || issue.issueType}</div>
				{issue.howToFix ? <p className="mt-0.5 text-xs text-muted-foreground">{issue.howToFix}</p> : null}
			</div>
			<div className="shrink-0 self-center text-sm font-bold tabular-nums">{(issue.issueCount ?? 0).toLocaleString()}</div>
			<button
				type="button"
				onClick={download}
				disabled={downloading}
				className="inline-flex shrink-0 self-center items-center gap-1.5 rounded-full bg-muted px-3.5 py-2 text-xs font-semibold text-foreground transition hover:bg-accent disabled:opacity-50"
			>
				Download <Download className="h-3 w-3" />
			</button>
		</div>
	);
}

export function FeedIssues({ shopId }) {
	const { issues } = useInventoryStatus(shopId);

	// Product-data issues are surfaced by ProfileCompletenessBar above (with
	// in-dashboard Fix CTAs); this section is the stock/ingest-file errors.
	const stockIssues = issues.filter((i) => i.feedLevel !== 'products');
	if (stockIssues.length === 0) return null;

	return (
		<div className="pt-4">
			<SectionHeader
				eyebrow="Feed health"
				title="Issues in your stock file"
				sub="Errors from your most recent upload. These come from your POS — fix them there and your next sync will clear them."
			/>
			<SoftCard className="overflow-hidden !p-0">
				<div className="divide-y divide-border/60">
					{stockIssues.map((issue) => (
						<IssueRow key={issue.issueType} shopId={shopId} issue={issue} />
					))}
				</div>
			</SoftCard>
		</div>
	);
}
